/**
 * Luhmann ID (L-ID) Generation and Manipulation Utilities
 *
 * L-ID Structure: [SectionID]-[SequenceID][BranchingCharacters...]
 * Internal format: 1-1, 1-1a, 1-1a1, 21-3d26g1i (dash separator)
 * Display format: 1/1, 1/1a, 1/1a1, 21/3d26g1i (slash for UI)
 *
 * Rules:
 * - Section ID: Top-level numeric identifier (e.g., 1, 2, 21)
 * - Sequence ID: Main sequence within section, starts with numbers
 * - Branching: Alternates between numbers and letters
 *   - Numbers: Deeper elaboration (1-1a → 1-1a1)
 *   - Letters: Parallel sub-ideas (1-1 → 1-1a, 1-1b)
 * - Max length: 13 characters (Luhmann's original constraint)
 */

export interface LidParts {
  sectionId: string
  sequence: string
}

export interface LidContext {
  parentLid: string | null
  siblings: string[]
  branchType: 'continue' | 'branch'
}

/**
 * Parse an L-ID into its component parts
 */
export function parseLid(lid: string): LidParts {
  const separatorIndex = lid.indexOf('-')
  if (separatorIndex === -1) {
    throw new Error(`Invalid L-ID format: ${lid}. Expected format: sectionId-sequence`)
  }

  return {
    sectionId: lid.substring(0, separatorIndex),
    sequence: lid.substring(separatorIndex + 1),
  }
}

/**
 * Compose an L-ID from its parts
 */
export function composeLid(parts: LidParts): string {
  return `${parts.sectionId}-${parts.sequence}`
}

/**
 * Convert internal LID format (dash) to display format (slash)
 * E.g., "1-1a" → "1/1a"
 */
export function formatLidForDisplay(lid: string): string {
  const dashIndex = lid.indexOf('-')
  if (dashIndex === -1) return lid
  return lid.substring(0, dashIndex) + '/' + lid.substring(dashIndex + 1)
}

/**
 * Normalize LID format - accepts both slash and dash, returns dash format
 * E.g., "1/1a" → "1-1a", "1-1a" → "1-1a"
 */
export function normalizeLid(lid: string): string {
  // If it has a slash, convert to dash
  const slashIndex = lid.indexOf('/')
  if (slashIndex !== -1) {
    return lid.substring(0, slashIndex) + '-' + lid.substring(slashIndex + 1)
  }
  return lid
}

/**
 * Check if a character is a letter (a-z)
 */
function isLetter(char: string): boolean {
  return /^[a-z]$/i.test(char)
}

/**
 * Check if a character is a digit (0-9)
 */
function isDigit(char: string): boolean {
  return /^[0-9]$/.test(char)
}

/**
 * Get the last character type of a sequence
 */
function getLastCharType(sequence: string): 'number' | 'letter' {
  if (sequence.length === 0) return 'number'
  const lastChar = sequence[sequence.length - 1]
  return isLetter(lastChar) ? 'letter' : 'number'
}

/**
 * Get the trailing segment of a sequence (the part that can be incremented)
 * E.g., "1a23" -> "23", "1a" -> "a", "123" -> "123"
 */
export function getTrailingSegment(sequence: string): string {
  if (sequence.length === 0) return ''

  const lastCharType = getLastCharType(sequence)
  let i = sequence.length - 1

  while (i > 0) {
    const charType = isLetter(sequence[i - 1]) ? 'letter' : 'number'
    if (charType !== lastCharType) break
    i--
  }

  return sequence.substring(i)
}

/**
 * Increment a letter segment (a -> b, z -> aa)
 */
function incrementLetterSegment(segment: string): string {
  if (segment.length === 0) return 'a'

  const chars = segment.split('')
  let i = chars.length - 1

  while (i >= 0) {
    if (chars[i] === 'z') {
      chars[i] = 'a'
      i--
    } else {
      chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1)
      return chars.join('')
    }
  }

  return 'a' + chars.join('')
}

/**
 * Increment a number segment (1 -> 2, 99 -> 100)
 */
function incrementNumberSegment(segment: string): string {
  if (segment.length === 0) return '1'
  return String(parseInt(segment, 10) + 1)
}

/**
 * Find all direct children of a parent L-ID
 */
export function findDirectChildren(parentLid: string, allLids: string[]): string[] {
  const parent = parseLid(parentLid)

  return allLids.filter((lid) => {
    if (lid === parentLid) return false

    try {
      const parts = parseLid(lid)
      if (parts.sectionId !== parent.sectionId) return false

      // Check if this lid starts with parent sequence
      if (!parts.sequence.startsWith(parent.sequence)) return false

      // Get the suffix after parent sequence
      const suffix = parts.sequence.substring(parent.sequence.length)
      if (suffix.length === 0) return false

      // Direct child should only have one "level" more
      // e.g., for parent "1", children are "1a", "1b", not "1a1"
      // for parent "1a", children are "1a1", "1a2", not "1a1a"

      // Determine expected next character type
      const parentLastType = getLastCharType(parent.sequence)
      const expectedNextType = parentLastType === 'number' ? 'letter' : 'number'

      // Check if suffix consists only of the expected type
      const suffixChars = suffix.split('')
      const firstCharType = isLetter(suffixChars[0]) ? 'letter' : 'number'

      if (firstCharType !== expectedNextType) return false

      // All characters in suffix should be same type for direct children
      return suffixChars.every((char) =>
        expectedNextType === 'letter' ? isLetter(char) : isDigit(char)
      )
    } catch {
      return false
    }
  })
}

/**
 * Find siblings of a given L-ID (L-IDs at the same level sharing the same parent)
 */
export function findSiblings(lid: string, allLids: string[]): string[] {
  const parentLid = getParentLid(lid)
  if (!parentLid) {
    // Root level siblings - same section, single number sequence
    const parts = parseLid(lid)
    return allLids.filter((l) => {
      try {
        const p = parseLid(l)
        return p.sectionId === parts.sectionId && /^\d+$/.test(p.sequence)
      } catch {
        return false
      }
    })
  }

  return findDirectChildren(parentLid, allLids)
}

/**
 * Get the parent L-ID of a given L-ID
 * E.g., "1/1a" -> "1/1", "1/1a1" -> "1/1a", "1/1" -> null (root)
 */
export function getParentLid(lid: string): string | null {
  const parts = parseLid(lid)
  const sequence = parts.sequence

  // Find where to cut to get parent
  let i = sequence.length - 1
  const lastCharType = getLastCharType(sequence)

  // Skip all characters of the same type at the end
  while (i > 0 && (isLetter(sequence[i - 1]) ? 'letter' : 'number') === lastCharType) {
    i--
  }

  if (i <= 0) {
    // This is a root sequence (e.g., "1", "2")
    return null
  }

  return composeLid({
    sectionId: parts.sectionId,
    sequence: sequence.substring(0, i),
  })
}

/**
 * Generate the next L-ID for a branch operation
 * Creates a new child branch from the parent
 */
export function generateBranchLid(parentLid: string, existingSiblings: string[]): string {
  const parent = parseLid(parentLid)
  const parentLastType = getLastCharType(parent.sequence)

  // New branch starts with opposite type
  const newCharType = parentLastType === 'number' ? 'letter' : 'number'

  // Find existing branches to determine the next one
  const branchSuffixes = existingSiblings
    .map((lid) => {
      try {
        const parts = parseLid(lid)
        if (parts.sectionId !== parent.sectionId) return null
        if (!parts.sequence.startsWith(parent.sequence)) return null

        const suffix = parts.sequence.substring(parent.sequence.length)
        if (suffix.length === 0) return null

        // Check if suffix is valid (all same type)
        const suffixChars = suffix.split('')
        const allSameType = suffixChars.every((char) =>
          newCharType === 'letter' ? isLetter(char) : isDigit(char)
        )

        return allSameType ? suffix : null
      } catch {
        return null
      }
    })
    .filter((s): s is string => s !== null)

  let nextSuffix: string
  if (branchSuffixes.length === 0) {
    nextSuffix = newCharType === 'letter' ? 'a' : '1'
  } else {
    // Find the maximum suffix and increment
    const maxSuffix = branchSuffixes.sort().pop()!
    nextSuffix =
      newCharType === 'letter'
        ? incrementLetterSegment(maxSuffix)
        : incrementNumberSegment(maxSuffix)
  }

  return composeLid({
    sectionId: parent.sectionId,
    sequence: parent.sequence + nextSuffix,
  })
}

/**
 * Generate the next L-ID for a continue operation
 * Creates a new sibling at the same level
 */
export function generateContinueLid(currentLid: string, existingSiblings: string[]): string {
  const current = parseLid(currentLid)
  const parentLid = getParentLid(currentLid)

  if (!parentLid) {
    // Root level - increment the number
    const maxSequence = existingSiblings
      .map((lid) => {
        try {
          const parts = parseLid(lid)
          if (parts.sectionId !== current.sectionId) return 0
          if (!/^\d+$/.test(parts.sequence)) return 0
          return parseInt(parts.sequence, 10)
        } catch {
          return 0
        }
      })
      .reduce((max, n) => Math.max(max, n), 0)

    return composeLid({
      sectionId: current.sectionId,
      sequence: String(maxSequence + 1),
    })
  }

  // Non-root level - use branch generation from parent
  return generateBranchLid(parentLid, existingSiblings)
}

/**
 * Generate a new L-ID based on context
 */
export function generateLid(context: LidContext, allLids: string[]): string {
  if (!context.parentLid) {
    throw new Error('parentLid is required for L-ID generation')
  }

  if (context.branchType === 'branch') {
    const siblings = findDirectChildren(context.parentLid, allLids)
    return generateBranchLid(context.parentLid, siblings)
  } else {
    const siblings = findSiblings(context.parentLid, allLids)
    return generateContinueLid(context.parentLid, siblings)
  }
}

/**
 * Create the first L-ID for a new section
 */
export function createFirstLid(sectionId: string): string {
  return `${sectionId}-1`
}

/**
 * Validate an L-ID format
 */
export function isValidLid(lid: string): boolean {
  try {
    const parts = parseLid(lid)

    // Section ID must be numeric
    if (!/^\d+$/.test(parts.sectionId)) return false

    // Sequence must start with a number
    if (!/^\d/.test(parts.sequence)) return false

    // Sequence must alternate between numbers and letters correctly
    // (This is a simplified check - full validation would be more complex)
    if (!/^(\d+([a-z]+\d+)*[a-z]*)$/.test(parts.sequence)) return false

    // Max length check (13 characters for original Luhmann constraint)
    if (lid.length > 14) return false // +1 for the separator

    return true
  } catch {
    return false
  }
}

/**
 * Sort L-IDs in logical order
 * Sorting follows the structural hierarchy
 */
export function sortLids(lids: string[]): string[] {
  return [...lids].sort((a, b) => {
    try {
      const partsA = parseLid(a)
      const partsB = parseLid(b)

      // First compare section IDs
      const sectionCompare = parseInt(partsA.sectionId) - parseInt(partsB.sectionId)
      if (sectionCompare !== 0) return sectionCompare

      // Then compare sequences character by character
      const seqA = partsA.sequence
      const seqB = partsB.sequence

      const minLen = Math.min(seqA.length, seqB.length)
      for (let i = 0; i < minLen; i++) {
        const charA = seqA[i]
        const charB = seqB[i]

        const isLetterA = isLetter(charA)
        const isLetterB = isLetter(charB)

        if (isLetterA !== isLetterB) {
          // Numbers come before letters in display order
          // But in structural order, the parent (number) comes first
          return isLetterA ? 1 : -1
        }

        if (isLetterA) {
          // Compare letters
          if (charA !== charB) return charA.localeCompare(charB)
        } else {
          // Compare numbers (need to parse full number segments)
          let numA = ''
          let numB = ''
          let j = i
          while (j < seqA.length && isDigit(seqA[j])) numA += seqA[j++]
          j = i
          while (j < seqB.length && isDigit(seqB[j])) numB += seqB[j++]

          const numCompare = parseInt(numA) - parseInt(numB)
          if (numCompare !== 0) return numCompare

          // Skip to end of number segment
          i = i + numA.length - 1
        }
      }

      // Shorter sequence comes first (parent before child)
      return seqA.length - seqB.length
    } catch {
      return a.localeCompare(b)
    }
  })
}

/**
 * Get the depth level of an L-ID
 * E.g., "1/1" -> 1, "1/1a" -> 2, "1/1a1" -> 3
 */
export function getLidDepth(lid: string): number {
  const parts = parseLid(lid)
  const sequence = parts.sequence

  let depth = 1
  let currentType: 'number' | 'letter' = 'number'

  for (const char of sequence) {
    const charType = isLetter(char) ? 'letter' : 'number'
    if (charType !== currentType) {
      depth++
      currentType = charType
    }
  }

  return depth
}

/**
 * Build a tree structure from a list of L-IDs
 */
export interface LidTreeNode {
  lid: string
  children: LidTreeNode[]
}

export function buildLidTree(lids: string[]): LidTreeNode[] {
  const sortedLids = sortLids(lids)
  const roots: LidTreeNode[] = []
  const nodeMap = new Map<string, LidTreeNode>()

  for (const lid of sortedLids) {
    const node: LidTreeNode = { lid, children: [] }
    nodeMap.set(lid, node)

    const parentLid = getParentLid(lid)
    if (parentLid && nodeMap.has(parentLid)) {
      nodeMap.get(parentLid)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}
